import React, { useState, useEffect } from 'react';
import { Close, QrCode, CheckCircle, Cancel } from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { initiatePayment, fetchPaymentProviders, getPosQRCodeUrl, makePayment } from '../../services/paymentService';
import { usePayment } from '../../contexts/PaymentContext';
import { useNotification } from '../../contexts/NotificationContext';
import { storageKeys } from '../../config/environment';

const safeParse = (value) => {
    try {
        return value ? JSON.parse(value) : null;
    } catch {
        return null;
    }
};

const CashierPaymentModal = ({ open, onClose, posId, cashRegisterId }) => {
    const { setCurrentPayment, paymentResponse } = usePayment();
    const { callbackMsg } = useNotification();
    const [step, setStep] = useState('form'); // 'form' | 'qr' | 'status'
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Formulaire
    const [amount, setAmount] = useState('');
    const [currency, setCurrency] = useState('CDF');
    const [motive, setMotive] = useState('');
    const [ref, setRef] = useState('');
    
    // Providers
    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [selectedProviderName, setSelectedProviderName] = useState('');
    const [account, setAccount] = useState('');
    const [countryCode, setCountryCode] = useState('243');
    const [isValidAccount, setIsValidAccount] = useState(false);
    
    // QR Code
    const [qrContent, setQrContent] = useState(null);
    const [paymentContent, setPaymentContent] = useState(null);

    const currencies = [
        { label: 'CDF', value: 'CDF' },
        { label: 'USD', value: 'USD' }
    ];

    const getProviderIcon = (providerName) => {
        const icons = {
            'Airtel Money': '📱',
            'Orange Money': '🟠',
            'Afri Money': '📲',
            'M Pesa': '💳',
            'Netikash Pay': '💰'
        };
        return icons[providerName] || '💵';
    };

    useEffect(() => {
        if (open && step === 'form') {
            loadProviders();
        }
    }, [open, step]);

    useEffect(() => {
        if (callbackMsg && callbackMsg.type === 'callback' && step === 'qr') {
            if (callbackMsg.status === 'approved') {
                setStep('status');
            } else if (['failed', 'canceled', 'expired'].includes(callbackMsg.status)) {
                setError(callbackMsg.message || 'Le paiement a échoué');
            }
        }
    }, [callbackMsg, step]);

    const loadProviders = async () => {
        try {
            const data = await fetchPaymentProviders();
            setProviders(data);
        } catch (error) {
            console.error('Error loading providers:', error);
        }
    };

    const validateAccount = (accountValue) => {
        if (!selectedProvider || !accountValue) {
            return false;
        }
        if (!selectedProvider.account_format) {
            return true;
        }
        try {
            const regex = new RegExp(selectedProvider.account_format);
            return regex.test(accountValue);
        } catch {
            return false;
        }
    };

    const handleProviderSelect = (providerName) => {
        const provider = providers.find((p) => p.name === providerName);
        setSelectedProvider(provider || null);
        setSelectedProviderName(providerName);
        setAccount('');
        setIsValidAccount(false);
    };

    const handleAccountChange = (value) => {
        setAccount(value);
        setIsValidAccount(validateAccount(value));
    };

    const handleInitiatePayment = async () => {
        if (!amount || parseFloat(amount) <= 0) {
            setError('Montant invalide');
            return;
        }
        
        if (!currency) {
            setError('Devise requise');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const dataToSend = {
                amount: parseFloat(amount),
                currency,
                motive: motive || '',
                ref: ref || '',
                company: ''
            };

            const content = await initiatePayment(
                posId,
                dataToSend,
                cashRegisterId
            );

            setPaymentContent(content);
            setQrContent(content.id);
            setCurrentPayment(content);
            setStep('qr');
        } catch (error) {
            setError(error.message || 'Erreur lors de l\'initiation du paiement');
        } finally {
            setLoading(false);
        }
    };

    const handleMakePayment = async () => {
        if (!selectedProvider || !account || !isValidAccount) {
            setError('Veuillez sélectionner un provider et entrer un compte valide');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const org = safeParse(localStorage.getItem(storageKeys.organisation));
            const solution = localStorage.getItem(storageKeys.solution);
            const security = {
                app_id: process.env.REACT_APP_APP_ID || '',
                client_id: process.env.REACT_APP_OAUTH_CLIENT_ID || ''
            };

            const paymentRqsModel = {
                company_id: org?.id || '',
                currency: currency,
                ref: paymentContent?.id || ref,
                app_id: security.app_id,
                client_id: security.client_id,
                label: 'paiement',
                amount: amount,
                callbackUrl: {
                    continue_url: 'com.netikash.netikashposapp',
                    callback_url: paymentContent?.callbackUrl?.confirm_url || ''
                },
                buyer: {
                    provider_id: selectedProvider.id,
                    prefix: countryCode,
                    account: account,
                    suffix: '',
                    full_name: '',
                    expiration_date: '',
                    control: ''
                }
            };

            await makePayment(paymentRqsModel, cashRegisterId);
            setStep('status');
        } catch (error) {
            setError(error.message || 'Erreur lors du paiement');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setStep('form');
        setAmount('');
        setCurrency('CDF');
        setMotive('');
        setRef('');
        setSelectedProvider(null);
        setSelectedProviderName('');
        setAccount('');
        setQrContent(null);
        setPaymentContent(null);
        setError('');
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {step === 'form' && (
                    <>
                        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-slate-900">Demander un paiement</h3>
                            <button onClick={handleClose} className="p-2 hover:bg-slate-100 rounded-xl">
                                <Close className="text-slate-500" />
                            </button>
                        </div>

                        <div className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Montant *</label>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500"
                                    placeholder="0.00"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Devise *</label>
                                <select
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500"
                                >
                                    {currencies.map((c) => (
                                        <option key={c.value} value={c.value}>{c.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">Motif (Optionnel)</label>
                                <input
                                    type="text"
                                    value={motive}
                                    onChange={(e) => setMotive(e.target.value)}
                                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-amber-500"
                                    placeholder="Raison du paiement"
                                />
                            </div>

                            {error && (
                                <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2 rounded-xl text-sm">
                                    {error}
                                </div>
                            )}

                            <div className="flex space-x-3 pt-2">
                                <button
                                    onClick={handleClose}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleInitiatePayment}
                                    disabled={loading || !amount}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50"
                                >
                                    {loading ? 'Génération...' : 'Générer QR Code'}
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {step === 'qr' && qrContent && (
                    <>
                        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                            <h3 className="text-xl font-semibold text-slate-900">Scanner pour payer</h3>
                            <button onClick={handleClose} className="p-2 hover:bg-slate-100 rounded-xl">
                                <Close className="text-slate-500" />
                            </button>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="flex justify-center">
                                <div className="bg-white p-4 rounded-2xl border-2 border-amber-200">
                                    <QRCodeSVG value={getPosQRCodeUrl(qrContent)} size={200} />
                                </div>
                            </div>

                            {paymentContent && (
                                <div className="space-y-2 text-center">
                                    <p className="text-lg font-semibold text-slate-900">
                                        {paymentContent.amount} {paymentContent.currency}
                                    </p>
                                    {paymentContent.motive && (
                                        <p className="text-sm text-slate-600">{paymentContent.motive}</p>
                                    )}
                                </div>
                            )}

                            {providers.length > 0 && (
                                <div>
                                    <p className="text-sm font-medium text-slate-700 mb-3">Ou payer avec :</p>
                                    <div className="flex flex-wrap gap-3 justify-center">
                                        {providers.map((provider) => (
                                            <button
                                                key={provider.id}
                                                onClick={() => handleProviderSelect(provider.name)}
                                                className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center space-y-1 ${
                                                    selectedProviderName === provider.name
                                                        ? 'border-amber-500 bg-amber-50'
                                                        : 'border-slate-200 hover:border-amber-300'
                                                }`}
                                                title={provider.name}
                                            >
                                                <span className="text-2xl">{getProviderIcon(provider.name)}</span>
                                                <span className="text-xs text-slate-600">{provider.name}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedProvider && selectedProvider.with_account && (
                                <div className="space-y-3">
                                    <div className="flex space-x-2">
                                        <input
                                            type="text"
                                            value={countryCode}
                                            onChange={(e) => setCountryCode(e.target.value)}
                                            className="w-20 px-3 py-2 border border-slate-300 rounded-xl"
                                            placeholder="+243"
                                            maxLength={4}
                                        />
                                        <input
                                            type="text"
                                            value={account}
                                            onChange={(e) => handleAccountChange(e.target.value)}
                                            className="flex-1 px-3 py-2 border border-slate-300 rounded-xl"
                                            placeholder={selectedProvider.account_label || 'Numéro de compte'}
                                        />
                                        {account && (
                                            isValidAccount ? (
                                                <CheckCircle className="text-emerald-500 text-2xl" />
                                            ) : (
                                                <Cancel className="text-rose-500 text-2xl" />
                                            )
                                        )}
                                    </div>
                                    {isValidAccount && (
                                        <button
                                            onClick={handleMakePayment}
                                            disabled={loading}
                                            className="w-full px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white font-semibold hover:from-emerald-600 hover:to-green-600 disabled:opacity-50"
                                        >
                                            {loading ? 'Paiement...' : 'Confirmer le paiement'}
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="flex space-x-3 pt-2">
                                <button
                                    onClick={() => setStep('form')}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50"
                                >
                                    Retour
                                </button>
                                <button
                                    onClick={handleClose}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600"
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {step === 'status' && (
                    <div className="p-6">
                        <div className="text-center">
                            <CheckCircle className="text-emerald-500 text-6xl mx-auto mb-4" />
                            <h3 className="text-xl font-semibold text-slate-900 mb-2">Paiement Validé !</h3>
                            <p className="text-slate-600 mb-6">Le paiement a été traité avec succès.</p>
                            <button
                                onClick={handleClose}
                                className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600"
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CashierPaymentModal;

